import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Star, Sparkles, Loader2, Copy, ExternalLink, Check, RefreshCcw,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuestReviewPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [config, setConfig] = useState(null); // { venue_name, pills, google_business_url, has_google_link }
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [googleUrl, setGoogleUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/reviews/sessions/${code}/config`);
        setConfig(r.data);
        setGoogleUrl(r.data.google_business_url || '');
      } catch (e) {
        setExpired(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const togglePill = (p) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const generate = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one highlight');
      return;
    }
    setGenerating(true);
    setCopied(false);
    try {
      const r = await axios.post(`${API}/reviews/sessions/${code}/generate`, {
        selected_pills: Array.from(selected),
      });
      const text = r.data.review_text || '';
      setReviewText(text);
      if (r.data.google_business_url) setGoogleUrl(r.data.google_business_url);
      // Auto-copy so guest can paste with zero extra taps after opening Google
      if (text) {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          toast.success('Review copied — tap "Post on Google" to paste & submit');
        } catch (err) {
          console.warn('[GuestReview] auto-copy failed:', err);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate review');
    } finally {
      setGenerating(false);
    }
  };

  // Convert any saved Google URL into the proper "writereview" deeplink format
  // when possible. This opens Google Maps directly on the review composer
  // instead of just the listing page, cutting taps from 3 → 1.
  const buildPostUrl = (raw) => {
    if (!raw) return '';
    // If we have a Place ID already, ideal — direct review deeplink
    const placeIdMatch = raw.match(/[?&]placeid=([^&]+)/i) || raw.match(/place_id=([^&]+)/i);
    if (placeIdMatch) {
      return `https://search.google.com/local/writereview?placeid=${placeIdMatch[1]}`;
    }
    // share.google short links / g.page links — Google will resolve and forward
    // to the canonical review composer if the listing supports reviews.
    return raw;
  };

  const postOnGoogle = async () => {
    const url = buildPostUrl(googleUrl);
    if (!url) {
      toast.error('Venue did not provide a Google link yet');
      return;
    }
    // Make sure the review is on the clipboard right before we open Google
    try {
      await navigator.clipboard.writeText(reviewText);
      setCopied(true);
    } catch (err) {
      console.warn('[GuestReview] clipboard write failed:', err);
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.info('Long-press the review box on Google → "Paste"');
  };

  const shareReview = async () => {
    if (!navigator.share) {
      toast.error('Sharing not supported on this browser — use Copy instead');
      return;
    }
    try {
      await navigator.share({
        title: `Review for ${config?.venue_name || 'the venue'}`,
        text: reviewText,
        ...(googleUrl ? { url: buildPostUrl(googleUrl) } : {}),
      });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('[GuestReview] share failed:', err);
      }
    }
  };

  const copyReview = async () => {
    try {
      await navigator.clipboard.writeText(reviewText);
      setCopied(true);
      toast.success('Copied! Now paste it on Google.');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('[GuestReview] clipboard copy failed:', err);
      toast.error('Could not copy. Long-press the text to copy manually.');
    }
  };

  const pasteOnGoogle = postOnGoogle;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center" data-testid="guest-review-loading">
        <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
      </div>
    );
  }

  if (expired || !config) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center p-6">
        <Toaster position="top-center" />
        <div className="text-center max-w-sm" data-testid="guest-review-expired">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-gray-500" />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">Reviews unavailable</h1>
          <p className="text-gray-400">
            This venue hasn&apos;t enabled the review experience yet, or the session has expired.
          </p>
          <Button
            variant="ghost"
            onClick={() => navigate(`/request/${code}`)}
            className="mt-6 border border-white/10 rounded-full text-xs text-gray-300 tracking-wider"
          >
            Back to session
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000] flex flex-col" data-testid="guest-review-page">
      <Toaster position="top-center" />

      <header className="px-4 py-4 border-b border-white/5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(`/request/${code}`)}
            className="text-gray-400 hover:text-white"
            data-testid="review-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-white font-semibold text-sm font-['Outfit']">Leave a Review</p>
            <p className="text-gray-500 text-xs">{config.venue_name}</p>
          </div>
          <div className="w-5" />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-10">
        <div className="max-w-lg mx-auto pt-6 space-y-6">
          {/* Hero */}
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-[#FFD700]/15 flex items-center justify-center ring-1 ring-[#FFD700]/30">
              <Sparkles className="w-7 h-7 text-[#FFD700]" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">
              Help us shine on Google
            </h1>
            <p className="text-gray-400 text-sm mt-1.5">
              Tap what you loved → we&apos;ll draft a quick review for you to paste on Google.
            </p>
          </div>

          {/* Pills */}
          {!reviewText && (
            <div data-testid="review-pills-container">
              <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mb-3">
                What stood out? <span className="normal-case text-gray-600">(pick a few)</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {(config.pills || []).map((pill) => {
                  const on = selected.has(pill);
                  return (
                    <button
                      key={pill}
                      onClick={() => togglePill(pill)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                        on
                          ? 'bg-[#FFD700] border-[#FFD700] text-black shadow-[0_0_24px_rgba(255,215,0,0.35)]'
                          : 'bg-white/5 border-white/10 text-gray-200 hover:border-[#FFD700]/40 hover:bg-[#FFD700]/5'
                      }`}
                      data-testid={`review-pill-${pill.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {pill}
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={generate}
                disabled={generating || selected.size === 0}
                className="w-full mt-6 h-12 bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold tracking-wider rounded-xl disabled:opacity-40"
                data-testid="review-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> WRITING…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> WRITE MY REVIEW</>
                )}
              </Button>
              <p className="text-[10px] text-gray-600 text-center mt-2">
                AI-assisted draft · Edit before posting if you like
              </p>
            </div>
          )}

          {/* Result */}
          {reviewText && (
            <div className="space-y-4" data-testid="review-result">
              <div
                className="p-5 rounded-2xl bg-white/[0.04] border border-[#FFD700]/30 shadow-[0_0_40px_rgba(255,215,0,0.08)]"
                data-testid="review-text-box"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                  ))}
                </div>
                <p className="text-white text-base leading-relaxed font-['Outfit']" data-testid="review-text">
                  {reviewText}
                </p>
              </div>

              {/* Auto-copy hint — review is already on the clipboard */}
              {copied && (
                <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30" data-testid="review-autocopied-hint">
                  <Check className="w-3.5 h-3.5 text-[#39FF14]" />
                  <p className="text-[11px] text-[#39FF14] font-bold uppercase tracking-[0.18em] font-['JetBrains_Mono']">
                    Review copied · tap below to post
                  </p>
                </div>
              )}

              <Button
                onClick={postOnGoogle}
                disabled={!googleUrl}
                className="w-full h-14 bg-[#FFD700] hover:bg-[#e6c200] text-black font-black tracking-[0.3em] rounded-2xl disabled:opacity-40 text-sm uppercase font-['Outfit'] shadow-[0_10px_40px_rgba(255,215,0,0.35)]"
                data-testid="review-open-google-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Post on Google →
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={copyReview}
                  variant="ghost"
                  className="h-11 border border-white/15 rounded-xl text-white font-semibold tracking-wider text-xs"
                  data-testid="review-copy-btn"
                >
                  {copied ? (
                    <><Check className="w-4 h-4 mr-2 text-[#39FF14]" /> COPIED</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> COPY AGAIN</>
                  )}
                </Button>
                <Button
                  onClick={shareReview}
                  variant="ghost"
                  className="h-11 border border-white/15 rounded-xl text-white font-semibold tracking-wider text-xs"
                  data-testid="review-share-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> SHARE
                </Button>
              </div>

              {!googleUrl && (
                <p className="text-[11px] text-amber-400/80 text-center" data-testid="review-no-google-warning">
                  This venue hasn&apos;t added their Google link yet. Copy the text and search the venue on Google to paste it.
                </p>
              )}

              <button
                onClick={() => { setReviewText(''); setSelected(new Set()); }}
                className="w-full text-xs text-gray-500 hover:text-gray-300 inline-flex items-center justify-center gap-1.5 pt-2"
                data-testid="review-regenerate-btn"
              >
                <RefreshCcw className="w-3 h-3" /> Start over with different highlights
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestReviewPage;
